import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { History, Mail, Send, RefreshCw, MessageCircle } from "lucide-react";
import { ActionHistory } from "@/components/client/ActionHistory";

const statusStyle = {
  sent: "text-sky-400 bg-sky-500/10 border-sky-500/20",
  delivered: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  mock: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  skipped: "text-slate-400 bg-slate-500/10 border-slate-500/20",
  failed: "text-red-400 bg-red-500/10 border-red-500/20",
};
const statusLabel = { sent: "Enviado", delivered: "Entregue", mock: "Simulado", skipped: "Ignorado", failed: "Falhou" };

export default function Logs() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [channel, setChannel] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 30;

  const load = useCallback(() => {
    api.get("/logs", { params: { channel, page, per_page: perPage } }).then((r) => setData(r.data));
  }, [channel, page]);
  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / perPage));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-100 flex items-center gap-2"><History className="h-7 w-7 text-sky-400" /> Histórico de Envios</h1>
          <p className="text-sm text-slate-400 mt-1">{data.total.toLocaleString("pt-BR")} disparos registrados</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 hover:text-white"><RefreshCw className="h-4 w-4" /></button>
      </div>

      <div data-testid="panel-actions-card" className="mb-6 rounded-xl border border-[#1F293D] bg-[#0F141F] p-4">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-200">Ações feitas no SYSLOG pelo CRM</h3>
        <div className="max-h-72 overflow-y-auto pr-1"><ActionHistory showClient /></div>
      </div>

      <div className="flex gap-2 mb-4">
        {[["", "Todos"], ["email", "E-mail"], ["sms", "SMS"], ["whatsapp", "WhatsApp"]].map(([v, l]) => (
          <button key={v} data-testid={`log-filter-${v || "all"}`} onClick={() => { setChannel(v); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${channel === v ? "bg-sky-500/15 text-sky-300 border-sky-500/30" : "bg-[#111827] text-slate-400 border-[#1F293D] hover:text-white"}`}>{l}</button>
        ))}
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-[#1F293D]">
              <th className="px-4 py-3 font-medium">Canal</th><th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">Destino</th><th className="px-4 py-3 font-medium">Gatilho</th>
              <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Quando</th></tr></thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id} className="border-b border-[#1F293D] hover:bg-[#151e30]">
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-slate-300">
                      {l.channel === "email" ? <Mail className="h-4 w-4 text-sky-400" /> : l.channel === "whatsapp" ? <MessageCircle className="h-4 w-4 text-[#25D366]" /> : <Send className="h-4 w-4 text-amber-400" />}
                      {{ email: "E-mail", whatsapp: "WhatsApp" }[l.channel] || "SMS"}{l.wa_status === "read" && <span className="text-[10px] text-sky-300">· lida</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-200">{l.client_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{l.to || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{l.trigger || "—"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusStyle[l.status] || statusStyle.skipped}`}>{statusLabel[l.status] || l.status}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(l.sent_at || "").replace("T", " ").slice(0, 16)}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Nenhum envio registrado.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#1F293D]">
          <span className="text-xs text-slate-500">Página {page} de {pages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 text-xs disabled:opacity-40">Anterior</button>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 text-xs disabled:opacity-40">Próxima</button>
          </div>
        </div>
      </div>
    </div>
  );
}
